import { FC, useState, Suspense, useMemo } from 'react';

import { useFormikContext, FieldArray } from 'formik';
import type { FieldArrayRenderProps } from 'formik';
import { useGetDatas } from 'modules/account/settings/hooks';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import type { DropResult } from 'react-beautiful-dnd';
import { IoCloseCircleOutline } from 'react-icons/io5';
import { Checkbox } from 'ui/components';
import { Button } from 'ui/components/common';
import { Searchbox } from 'ui/components/form';
import { SwitcherIcon } from 'ui/components/icon';
import useModal from 'ui/partials/Modal/useModal';
import { cn } from 'utils/helpers';
import { useProfileContext } from 'utils/providers/Profile';
import { DATA_RECORD_SOURCE } from 'utils/types';
import { startCase } from 'lodash';
import { DEAL_SCHEMA } from 'modules/account/sales/forms/schema';

import {
  COMPANY_SCHEMA,
  EXPORT_DATA_CONFIGURATOR_FORM,
  LEAD_SCHEMA,
} from './schema';

interface PROPS {
  onClose?: () => void;
  source: DATA_RECORD_SOURCE;
  defaultRows?: { label: string; value: string }[];
}

const ExportDataForm: FC<PROPS> = ({ source, defaultRows = [], onClose }) => {
  const {
    state: { profile },
  } = useProfileContext();

  const { hideModal } = useModal();
  const { data } = useGetDatas({ accountId: profile?.accountId, source });

  const { values, resetForm, handleSubmit } =
    useFormikContext<EXPORT_DATA_CONFIGURATOR_FORM>();
  const [searchQuery, setSearchQuery] = useState('');

  const selected = values.selected;

  const defaultFieldsListBySource =
    useMemo(() => {
      switch (source) {
        case DATA_RECORD_SOURCE.LEAD: {
          const fields = [{ label: 'ID', type: 'key', value: '_id' }].concat(
            Object.keys(
              LEAD_SCHEMA.omit([
                'isSubCustomer',
                'nameToPrintOnCheck',
                'companyName',
                'data',
                'tagId',
                'accountStatusId',
              ]).fields,
            ).map((key) => ({
              label: startCase(key),
              value: key,
              type: 'key',
            })),
          );
          return fields;
        }
        case DATA_RECORD_SOURCE.COMPANY: {
          const fields = [{ label: 'ID', type: 'key', value: '_id' }].concat(
            Object.keys(COMPANY_SCHEMA.omit(['data']).fields).map((key) => ({
              label: startCase(key),
              value: key,
              type: 'key',
            })),
          );
          return fields;
        }
        case DATA_RECORD_SOURCE.DEAL: {
          const fields = [{ label: 'ID', type: 'key', value: '_id' }].concat(
            Object.keys(DEAL_SCHEMA.omit(['data']).fields).map((key) => ({
              label: startCase(key),
              value: key,
              type: 'key',
            })),
          );
          return fields;
        }
      }
    }, [source])?.filter((val) =>
      val.label.toLowerCase().includes(searchQuery.toLowerCase()),
    ) ?? [];

  const customFieldsList: { label: string; value: string }[] =
    data?.data
      .map((d) => ({
        label: d.name,
        value: d._id,
        type: 'dataId',
      }))
      ?.filter((val) =>
        val.label.toLowerCase().includes(searchQuery.toLowerCase()),
      ) ?? [];

  const handleDragEnd = (
    result: DropResult,
    arrayHelpers: FieldArrayRenderProps,
  ) => {
    const from = result.source.index;
    const to = result.destination?.index as number;
    arrayHelpers.move(from, to);
  };

  return (
    <Suspense fallback={null}>
      <FieldArray
        name="selected"
        render={(arrayHelpers) => (
          <form
            onSubmit={handleSubmit}
            className="min-h-[500px] flex flex-col justify-between"
          >
            <div className="flex gap-5  flex-1">
              <div className="space-y-5 flex-1 max-w-[324px] flex flex-col p-4">
                <Searchbox
                  placeholder="Search fields"
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                />

                <div className="flex flex-col flex-1 gap-2">
                  <div>
                    <div>
                      <div
                        className={cn(
                          `text-sm leading-[22px] tracking-[0.04em]
                   font-semibold pb-3 border-b border-b-separator-primary`,
                        )}
                      >
                        Default Fields
                      </div>
                    </div>
                    <div
                      className={cn(
                        'overflow-auto flex-1 flex flex-col space-y-4',
                        'py-3 max-h-[300px] scrollbar-thin',
                      )}
                    >
                      {defaultFieldsListBySource?.map?.((item) => {
                        const selectedIndex = selected?.findIndex(
                          (v) => v.value === item.value,
                        ) as number;
                        const isSelected = (selectedIndex ?? 0) >= 0;
                        return (
                          <Checkbox
                            key={item.value}
                            onClick={() => {
                              if (isSelected) {
                                return arrayHelpers.remove(selectedIndex);
                              } else {
                                arrayHelpers.push(item);
                              }
                            }}
                            label={item.label}
                            checked={isSelected}
                          />
                        );
                      }) ?? 'No custom fields yet'}
                    </div>
                  </div>
                  <div>
                    <div>
                      <div
                        className={cn(
                          `text-sm leading-[22px] tracking-[0.04em]
                   font-semibold pb-3 border-b border-b-separator-primary`,
                        )}
                      >
                        Custom Fields
                      </div>
                    </div>
                    <div
                      className={cn(
                        'overflow-auto flex-1 flex flex-col space-y-4',
                        'py-3 max-h-[300px] scrollbar-thin',
                      )}
                    >
                      {customFieldsList?.map?.((item) => {
                        const selectedIndex = selected?.findIndex(
                          (v) => v.value === item.value,
                        ) as number;
                        const isSelected = (selectedIndex ?? 0) >= 0;
                        return (
                          <Checkbox
                            key={item.value}
                            onClick={() => {
                              if (isSelected) {
                                return arrayHelpers.remove(selectedIndex);
                              } else {
                                arrayHelpers.push(item);
                              }
                            }}
                            label={item.label}
                            checked={isSelected}
                          />
                        );
                      }) ?? 'No custom fields yet'}
                    </div>
                  </div>
                </div>
              </div>
              <DragDropContext
                onDragEnd={(result) => handleDragEnd(result, arrayHelpers)}
              >
                <Droppable droppableId="selected">
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      className="flex-1 bg-fill-secondary p-4 "
                    >
                      <div
                        className={cn(
                          'h-9 flex font-semibold border-b border-b-separator-secondary',
                          'flex justify-between items-center',
                        )}
                      >
                        <div className="text-sm  leading-[22px] ">
                          Selected Fields ({selected?.length})
                        </div>
                        <Button
                          label={'Clear'}
                          intent={'link'}
                          className="p-0"
                          disabled={!selected?.length}
                          onClick={() =>
                            resetForm({ values: { selected: [] } })
                          }
                        />
                      </div>
                      <div className="space-y-[6px] pt-3 h-full overflow-auto scrollbar-thin">
                        {defaultRows?.map((row) => (
                          <div
                            className={cn(
                              'flex',
                              'items-center',
                              'justify-between',
                              'rounded-[6px]',
                              'border',
                              'border-secondary-lightest pl-3 pr-2',
                              'group',
                              'bg-[#F1F4F8]',
                              'h-[36px]',
                            )}
                          >
                            <div
                              key={row.label}
                              className="flex items-center gap-2  text-sm"
                            >
                              <div>{row.label}</div>
                            </div>
                          </div>
                        ))}
                        {selected?.map((item, index) => {
                          return (
                            <Draggable
                              key={item.value}
                              draggableId={`${item.value}`}
                              index={index}
                            >
                              {(provided) => {
                                return (
                                  <div
                                    className={cn(
                                      'flex',
                                      'items-center',
                                      'justify-between',
                                      'rounded-[6px]',
                                      'border',
                                      'border-secondary-lightest pl-3 pr-2',
                                      'group',
                                      'bg-fill-tertiary',
                                      'h-[36px]',
                                    )}
                                    ref={provided.innerRef}
                                    {...provided.dragHandleProps}
                                    {...provided.draggableProps}
                                  >
                                    <div className="flex items-center gap-2  text-sm">
                                      <SwitcherIcon className="w-2 h-2" />
                                      <div>{item.label}</div>
                                    </div>
                                    <div
                                      className={cn(
                                        'flex items-center gap-2 relative',
                                      )}
                                    >
                                      <button
                                        type="button"
                                        onClick={() => {
                                          arrayHelpers.move(index, 0);
                                        }}
                                        className={cn(
                                          'opacity-0 group-hover:opacity-100 ',
                                          'hover:text-primary-base transition text-xs',
                                        )}
                                      >
                                        Move to top
                                      </button>
                                      <IoCloseCircleOutline
                                        onClick={() =>
                                          arrayHelpers.remove(index)
                                        }
                                        className="w-4 h-4 text-content hover:text-danger-base"
                                        role="button"
                                      />
                                    </div>
                                  </div>
                                );
                              }}
                            </Draggable>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
            <div
              className={cn(
                'flex justify-end gap-3',
                'px-2.5 py-4',
                'border-t-[0.5px] border-separator-primary',
              )}
            >
              <div className="flex gap-3 items-center">
                <Button
                  disabled={!selected?.length}
                  type="submit"
                  label="EXPORT"
                  size="sm"
                />
                <Button
                  onClick={() => {
                    resetForm();
                    hideModal();
                    onClose?.();
                  }}
                  label="CANCEL"
                  size="sm"
                  intent="tertiary"
                />
              </div>
            </div>
          </form>
        )}
      />
    </Suspense>
  );
};

export default ExportDataForm;
